// src/com/trans_plan_tsr/TransPlanTsrEditorGroup.js
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import {
    Button, DialogContent, DialogActions, AppBar, Tabs, Tab
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { AppWord, AppObject, AppStyle, TopCode } from '../../value/AppConst';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import DraggableEditor from '../../common/DraggableEditor';
import TransPlanTsrEditDetail from './TransPlanTsrEditDetail';
import TransPlanTsrEditSupplyGen982 from './TransPlanTsrEditSupplyGen982';
import TransPlanTsrEditSupplyGeneral from './TransPlanTsrEditSupplyGeneral';
import TransPlanTsrEditSupplyFresh from './TransPlanTsrEditSupplyFresh';
import TransPlanTsrEditSupplyExpress from './TransPlanTsrEditSupplyExpress';
import TransPlanTsrEditBulkDetail from './TransPlanTsrEditBulkDetail';
import { ShowDebugRepo, userInfoRepo } from '../../value/AppModel';
import { useReactiveVar } from '@apollo/client';

const LabelBox = styled(Box)({
    display: 'inline-flex', alignItems: 'center', height: 40,
    borderRadius: 20, padding: '0px 15px', backgroundColor: AppStyle.PrimaryRGB, color: 'white',
});

export const TabCodeForTsrEditorGroup = {
    DetailEditor: 1,
    SupplyEditor: 2,
    BulkEditor: 3,
};

/**
 * 3종류의 편집기를 Tab으로 구성한 편집기 그룹
 * 1. 상차 상세 내역
 * 2. 물류 집기 - 상온, 냉장, Express 각각 서로 다른 구성.
 * 3. Bulk 상차 상세내역 (특정 DC만 사용)
 * 저장, 취소 버튼은 이 컴포넌트에서 처리. (자식 컴포넌트에서는 변경사항만 보냄)
 * @param {*} param0 
 */
export default function TransPlanTsrEditorGroup({
    open,
    item,
    tabSelected,
    onClose,
    onRequestSave,
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const showDebug = useReactiveVar(ShowDebugRepo);
    const [tabNow, setTabNow] = useState(TabCodeForTsrEditorGroup.DetailEditor);
    const [data, setData] = useState({}); // child에서 변경함.
    const [errorMap, setErrorMap] = useState({});
    const [changed, setChanged] = useState(false); // if setData is called, set true
    const [hasError, setHasError] = useState(false);
    const openConfirmDialog = useConfirmDialog();

    useEffect(()=>{
        if(open) {
            //const ymdHms = ValueMisc.getYmdHms(new Date());
            /* const newData = {...item};
            if(!newData.prosDptrTime) newData.prosDptrTime = newData.dptrReqTime || ymdHms;
            if(!newData.prosArrTime) newData.prosArrTime = newData.arrReqTime || ymdHms;
            if(!newData.loadStTime) newData.loadStTime = ymdHms;
            if(!newData.loadEnTime) newData.loadEnTime = ymdHms; */
            setData({...item});
            setChanged(false);
            setErrorMap({});
            setHasError(false);
        }
    }, [open, item]);

    useEffect(()=>{
        if(open && Object.values(TabCodeForTsrEditorGroup).includes(tabSelected)) {
            setTabNow(tabSelected);
        }
    }, [open, tabSelected]);

    const onChangeData = (id, value, error) => {
        const newData = {...data, [id]: value};
        const newErrorMap = {...errorMap, [id]: error}; // error is boolean
        setErrorMap(newErrorMap);
        // 직전 errorMap(stale state)이 아닌 이번 변경이 반영된 newErrorMap 기준으로 계산해야
        // 에러가 해소된 직후에도 hasError가 true로 남아 임시저장 버튼이 계속 비활성화되지 않는다.
        setHasError(Object.values(newErrorMap).some(v => v));
        setChanged(true);
        setData(newData);
    };

    const onClickSaveInMemory = () => {
        onRequestSave(data);
    };

    const askCancel = async () => {
        const confirm = await openConfirmDialog({
            ...AppObject.messageCancelEdit,
            option: {yesLabel: AppWord.IgnoreAndClose}
        });
        if(confirm) onClose();
    };

    const onClickCancel = () => {
        if(changed) askCancel();
        else onClose();
    };

    const onRequestNextView = () => {
        if(tabNow === TabCodeForTsrEditorGroup.DetailEditor) {
            setTabNow(TabCodeForTsrEditorGroup.SupplyEditor);
        }
        else if(tabNow === TabCodeForTsrEditorGroup.SupplyEditor) {
            if(item.dcCd === TopCode.DcCode.Adc982) {
                setTabNow(TabCodeForTsrEditorGroup.BulkEditor);
            }
        }
    };

    const renderByTab = () => {
        if(tabNow===TabCodeForTsrEditorGroup.SupplyEditor) {
            if(item.dcCd===TopCode.DcCode.Adc982) {
                return (
                    <TransPlanTsrEditSupplyGen982 item={data}
                        onChangeValue={onChangeData}
                        onRequestNextView={onRequestNextView}
                    />
                );
            }
            if(item.dcTypeCd===TopCode.DcTypeCode.Fresh) {
                return <TransPlanTsrEditSupplyFresh item={data} onChangeValue={onChangeData} />;
            }
            if(item.dcTypeCd===TopCode.DcTypeCode.Express) {
                return <TransPlanTsrEditSupplyExpress item={data} onChangeValue={onChangeData} />;
            }
            return <TransPlanTsrEditSupplyGeneral item={data} onChangeValue={onChangeData} />;
        }
        if(tabNow===TabCodeForTsrEditorGroup.BulkEditor) {
            return (
                <TransPlanTsrEditBulkDetail item={data}
                    onChangeValue={onChangeData}
                />
        );
        }
        // default 상차 상세내역
        return (
            <TransPlanTsrEditDetail item={data}
                onChangeValue={onChangeData}
                onRequestNextView={onRequestNextView}
            />
        );
    };

    // item.vehTypeName + ', ' + item.loadCapaText
    return(
        <DraggableEditor open={open} maxWidth='lg'
            onClose={onClickCancel}
            title="상차 상세내역, 물류 집기"
            startIcon={<LocalShippingIcon size={AppWord.LARGE} />}
        >
            <DialogContent>
                <Box
                    style={{
                        padding: '10px 0px 15px 0px',
                        display: 'flex', alignItems: 'center',
                    }}
                >
                    <LabelBox>{data.dcName}</LabelBox>.........
                    <LabelBox style={{backgroundColor: AppStyle.SecondaryRGB}}>
                        <LocalShippingIcon fontSize={AppWord.SMALL} style={{marginRight: 5}} />
                        {data.hocha}호차
                    </LabelBox>
                    <KeyboardDoubleArrowRightIcon fontSize={AppWord.SMALL} />
                    <LabelBox>
                        <StorefrontIcon fontSize={AppWord.SMALL} style={{marginRight: 5}} />
                        {data.storeName}
                    </LabelBox>
                    {
                        data.dcTypeCd === TopCode.DcTypeCode.General ?
                        <Box
                            style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                marginLeft: 15, minWidth: 200,
                                padding: '0px 15px', height: 40,
                                borderRadius: 20, border: '1px solid ' + AppStyle.PrimaryRGB,
                                color: AppStyle.PrimaryRGB,
                            }}
                        >
                            {data.vehTypeName}, {data.loadCapaText}
                        </Box>
                        : null
                    }
                </Box>
                <AppBar position="static" color="default">
                    <Tabs value={tabNow} onChange={(e, v)=>setTabNow(v)}>
                        <Tab value={TabCodeForTsrEditorGroup.DetailEditor}
                            label="상차 상세내역" />
                        <Tab value={TabCodeForTsrEditorGroup.SupplyEditor}
                            label="물류 집기" />
                        {
                            data.dcCd===TopCode.DcCode.Adc982 &&
                            <Tab value={TabCodeForTsrEditorGroup.BulkEditor}
                                label="Bulk 상차 상세내역" />
                        }
                    </Tabs>
                </AppBar>
                <Box
                    style={{
                        minHeight: 520,
                        minWidth: 890,
                        paddingTop: 15, position: 'relative',
                    }}
                >
                    {data.planId && renderByTab()}
                </Box>
            </DialogContent>
            <DialogActions style={{display:'flex'}}>
                {
                    showDebug &&
                    <Button
                        variant={AppStyle.mui.contained}
                        onClick={() => {
                            console.log('Error map:', errorMap);
                        }}
                    >
                        Debug
                    </Button>
                }
                {
                    /*  수시로 정보 수정이 필요. 임시저장 및 TSR저장 버튼은 항시 활성화 바람. 2026.6.24 */
                    sessionInfo.isHomeplusOrEtrace() && onRequestSave &&
                    <Button
                        onClick={onClickSaveInMemory}
                        disabled={hasError}
                        variant={AppStyle.mui.contained}
                        color={AppStyle.mui.primary}
                    >임시저장</Button>
                }
                <Button onClick={onClickCancel}
                    color={AppStyle.mui.warning}
                    variant={AppStyle.mui.contained}
                >
                    {sessionInfo.isHomeplusOrEtrace() && onRequestSave ? '취소' : '닫기'}
                </Button>
            </DialogActions>
        </DraggableEditor>
    );
}